import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { apiService } from '../services/apiService';

type Policy = {
  passwordMinLength: number;
  passwordRequireUppercase: boolean;
  passwordRequireLowercase: boolean;
  passwordRequireDigit: boolean;
  passwordRequireSpecial: boolean;
  mfaEnabled: boolean;
  sessionTimeoutMinutes: number;
  ipWhitelist: string[];
  ipBlocklist: string[];
  suspiciousFailedLoginThreshold: number;
  suspiciousWindowMinutes: number;
};

const defaults: Policy = {
  passwordMinLength: 16,
  passwordRequireUppercase: true,
  passwordRequireLowercase: true,
  passwordRequireDigit: true,
  passwordRequireSpecial: true,
  mfaEnabled: true,
  sessionTimeoutMinutes: 600,
  ipWhitelist: [],
  ipBlocklist: [],
  suspiciousFailedLoginThreshold: 10,
  suspiciousWindowMinutes: 15,
};

function listToTextarea(list: string[]) {
  return (list || []).join('\n');
}

function textareaToList(value: string) {
  return value
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

const SuperAdminSecurityControlsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [policy, setPolicy] = useState<Policy>(defaults);

  const whitelistText = useMemo(() => listToTextarea(policy.ipWhitelist), [policy.ipWhitelist]);
  const blocklistText = useMemo(() => listToTextarea(policy.ipBlocklist), [policy.ipBlocklist]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getAdminSecurityPolicy();
      setPolicy({ ...defaults, ...(res || {}) });
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load security policy');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        ...policy,
        ipWhitelist: policy.ipWhitelist || [],
        ipBlocklist: policy.ipBlocklist || [],
      };
      const res = await apiService.updateAdminSecurityPolicy(payload);
      setPolicy({ ...defaults, ...(res || {}) });
      setSuccess('Saved');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save security policy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <SecurityIcon />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Security Controls
        </Typography>
      </Box>

      {loading && <CircularProgress size={22} />}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Password Policy
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <TextField
            size="small"
            type="number"
            label="Min length"
            value={policy.passwordMinLength}
            onChange={(e) => setPolicy((p) => ({ ...p, passwordMinLength: Number(e.target.value) }))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={policy.passwordRequireUppercase}
                onChange={(e) => setPolicy((p) => ({ ...p, passwordRequireUppercase: e.target.checked }))}
              />
            }
            label="Require uppercase"
          />
          <FormControlLabel
            control={
              <Switch
                checked={policy.passwordRequireLowercase}
                onChange={(e) => setPolicy((p) => ({ ...p, passwordRequireLowercase: e.target.checked }))}
              />
            }
            label="Require lowercase"
          />
          <FormControlLabel
            control={
              <Switch
                checked={policy.passwordRequireDigit}
                onChange={(e) => setPolicy((p) => ({ ...p, passwordRequireDigit: e.target.checked }))}
              />
            }
            label="Require digit"
          />
          <FormControlLabel
            control={
              <Switch
                checked={policy.passwordRequireSpecial}
                onChange={(e) => setPolicy((p) => ({ ...p, passwordRequireSpecial: e.target.checked }))}
              />
            }
            label="Require special"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          MFA (OTP)
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={policy.mfaEnabled}
              onChange={(e) => setPolicy((p) => ({ ...p, mfaEnabled: e.target.checked }))}
            />
          }
          label="Require OTP for login/register"
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Network Controls
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <TextField
            label="IP Whitelist (one per line)"
            value={whitelistText}
            onChange={(e) => setPolicy((p) => ({ ...p, ipWhitelist: textareaToList(e.target.value) }))}
            multiline
            minRows={6}
          />
          <TextField
            label="IP Blocklist (one per line)"
            value={blocklistText}
            onChange={(e) => setPolicy((p) => ({ ...p, ipBlocklist: textareaToList(e.target.value) }))}
            multiline
            minRows={6}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Sessions & Detection
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <TextField
            size="small"
            type="number"
            label="Session timeout (minutes)"
            value={policy.sessionTimeoutMinutes}
            onChange={(e) => setPolicy((p) => ({ ...p, sessionTimeoutMinutes: Number(e.target.value) }))}
          />
          <TextField
            size="small"
            type="number"
            label="Failed login threshold"
            value={policy.suspiciousFailedLoginThreshold}
            onChange={(e) => setPolicy((p) => ({ ...p, suspiciousFailedLoginThreshold: Number(e.target.value) }))}
          />
          <TextField
            size="small"
            type="number"
            label="Window (minutes)"
            value={policy.suspiciousWindowMinutes}
            onChange={(e) => setPolicy((p) => ({ ...p, suspiciousWindowMinutes: Number(e.target.value) }))}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="outlined" disabled={saving} onClick={load}>
            Reload
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SuperAdminSecurityControlsPage;

